import { useState } from "react";
import { Flame, Beef, Wheat, Droplet, Leaf } from "lucide-react";
import { computeRecipeTotals, computePer100g } from "../lib/nutrition";

const Row = ({ icon: Icon, label, value, unit, color, testId }) => (
  <div className="flex items-center justify-between py-3 border-b border-emerald-800/40 last:border-0">
    <div className="flex items-center gap-2.5">
      <span
        className="h-8 w-8 rounded-lg grid place-items-center"
        style={{ backgroundColor: color + "22", color }}
      >
        <Icon className="h-4 w-4" />
      </span>
      <span className="text-sm text-emerald-100 font-medium">{label}</span>
    </div>
    <div className="text-right">
      <div className="text-xl font-semibold text-white tabular-nums" data-testid={testId}>
        {value}
      </div>
      <div className="text-[10px] tracking-widest uppercase text-emerald-300">{unit}</div>
    </div>
  </div>
);

export const SummaryWidget = ({ ingredients, recipeName }) => {
  const [mode, setMode] = useState("total"); // 'total' | 'per100'

  const totals = computeRecipeTotals(ingredients);
  const per100 = computePer100g(totals);
  const isPer100 = mode === "per100";
  const t = isPer100 ? per100 : totals;
  const valueUnit = isPer100 ? "g / 100 g" : "grammes";

  // Macro split (uses totals — proportions identical for per-100g)
  const macroSum = totals.protein * 4 + totals.carbs * 4 + totals.fat * 9;
  const empty = macroSum <= 0;
  const total = empty ? 1 : macroSum;
  const proteinPct = empty ? 0 : Math.round(((totals.protein * 4) / total) * 100);
  const carbsPct = empty ? 0 : Math.round(((totals.carbs * 4) / total) * 100);
  const fatPct = empty ? 0 : Math.max(0, 100 - proteinPct - carbsPct);

  return (
    <aside
      className="bg-emerald-900 text-white rounded-2xl p-6 sm:p-7 shadow-[0_8px_32px_rgba(0,0,0,0.12)] lg:sticky lg:top-24"
      data-testid="summary-widget"
    >
      <div className="text-emerald-200 text-[10px] tracking-[0.22em] uppercase mb-3 font-bold">
        Résumé nutritionnel
      </div>
      <div className="font-heading text-2xl font-extrabold mb-1 leading-tight">
        {recipeName?.trim() || "Recette sans titre"}
      </div>
      <div className="text-emerald-300 text-xs mb-4">
        Poids total : <span className="text-emerald-100 font-semibold">{totals.weight_g} g</span>
      </div>

      {/* Mode toggle: Total / Per 100g (INCO) */}
      <div
        className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-emerald-950/50 mb-5"
        data-testid="summary-mode-toggle"
      >
        <button
          type="button"
          onClick={() => setMode("total")}
          className={`text-[11px] font-bold tracking-wider uppercase py-2 rounded-md transition-colors ${
            !isPer100
              ? "bg-emerald-700 text-white shadow"
              : "text-emerald-300 hover:text-white"
          }`}
          data-testid="mode-total-btn"
        >
          Total
        </button>
        <button
          type="button"
          onClick={() => setMode("per100")}
          className={`text-[11px] font-bold tracking-wider uppercase py-2 rounded-md transition-colors ${
            isPer100
              ? "bg-emerald-700 text-white shadow"
              : "text-emerald-300 hover:text-white"
          }`}
          data-testid="mode-per100-btn"
          title="Valeurs pour 100 g de produit fini (norme INCO)"
        >
          Pour 100 g
        </button>
      </div>

      <div className="bg-emerald-950/40 rounded-xl p-5 mb-5">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-emerald-200 text-[10px] tracking-widest uppercase font-bold mb-1">
              Calories
            </div>
            <div className="text-4xl font-light tabular-nums" data-testid="summary-calories">
              {t.calories}
            </div>
            <div className="text-emerald-300 text-xs mt-1">
              {isPer100 ? "kcal / 100 g" : "kcal au total"}
            </div>
          </div>
          <Flame className="h-10 w-10 text-amber-400" />
        </div>
        <div className="mt-4 flex h-2 w-full overflow-hidden rounded-full bg-emerald-950/60">
          <div className="bg-emerald-400" style={{ width: `${proteinPct}%` }} />
          <div className="bg-amber-400" style={{ width: `${carbsPct}%` }} />
          <div className="bg-red-400" style={{ width: `${fatPct}%` }} />
        </div>
        <div className="mt-2 flex justify-between text-[11px] text-emerald-200">
          <span>P {proteinPct}%</span>
          <span>G {carbsPct}%</span>
          <span>L {fatPct}%</span>
        </div>
      </div>

      <Row icon={Beef} label="Protéines" value={t.protein} unit={valueUnit} color="#34d399" testId="summary-protein" />
      <Row icon={Wheat} label="Glucides" value={t.carbs} unit={valueUnit} color="#fbbf24" testId="summary-carbs" />
      <div
        className="flex items-center justify-between py-2 pl-11 pr-1 border-b border-emerald-800/40"
        data-testid="summary-sugars-row"
      >
        <span className="text-xs italic text-emerald-200">dont sucres</span>
        <span className="text-sm font-medium text-emerald-100 tabular-nums" data-testid="summary-sugars">
          {t.sugars}
          <span className="text-[10px] tracking-widest uppercase text-emerald-300 ml-1">
            {isPer100 ? "/ 100 g" : "g"}
          </span>
        </span>
      </div>
      <Row icon={Droplet} label="Lipides" value={t.fat} unit={valueUnit} color="#f87171" testId="summary-fat" />
      <Row icon={Leaf} label="Fibres" value={t.fiber} unit={valueUnit} color="#a3e635" testId="summary-fiber" />

      {isPer100 && (
        <div className="mt-4 text-[10px] text-emerald-300 italic leading-relaxed">
          Format conforme au règlement européen INCO (UE) n° 1169/2011 — valeurs pour 100 g de produit fini.
        </div>
      )}
    </aside>
  );
};

export default SummaryWidget;
